/** @jsxImportSource ./simboluri-jsx */
// The section -> component mapping of the old structure App.tsx (its
// parseHash switch), as a routed component (wave 2).
//
// One component serves every structure-owned subpage of a solution row
// (contractul «soluțiile pe faze», §8):
//
//   /p/:pid/structura/:sol/pas/:section    section from the URL (step keys
//                                          plus the step-hosted pages «acasa»,
//                                          «imbunatatiri», «elemente», «fisa»)
//   /p/:pid/structura/:sol/element/:ref    section="element"
//   /p/:pid/verificari/:sol/tip/:tip       section="verificare"
//
// Data comes from StructureContext, which reads `:sol` and `?corp=`; `?focus=`
// and `?nivel=` keep their old meaning, `?bp=` names a frozen Pasul 4 state.
// Corpul se alege din meniul vizualizatorului — bara de corpuri de deasupra
// pașilor a plecat (A6).

import { useMemo } from "react";
import {
  useLocation,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useStructure } from "./StructureContext";
import { useSchitaFem } from "./schitaFem";
import ObsHost from "./ObsHost";
import { STEPS, titluPas, type Focus } from "./legaturi";
import { pasulFundatiei } from "./fundatia";
import { EroareSolutie } from "../nav/BlocSolutie";
import { useRegistruOptional } from "../nav/Registru";
import { caleEtapa } from "../nav/selectie";

import Acasa from "./Acasa";
import ElementPage from "./ElementPage";
import MarcaArmaturaPage from "./MarcaArmaturaPage";
import ElementeLista from "./ElementeLista";
import FisaSolutiePage from "./FisaSolutiePage";
import Imbunatatiri from "./Imbunatatiri";
import PasAntet from "./PasAntet";
import TitluPas from "./TitluPas";
import { indexClase } from "./claseElemente";
import VerificareTipPage from "./VerificareTipPage";

import StepArhitectura from "./StepArhitectura";
import StepArmareFundatie from "./StepArmareFundatie";
import StepArmareGrinzi from "./StepArmareGrinzi";
import StepArmarePereti from "./StepArmarePereti";
import StepArmarePlaca from "./StepArmarePlaca";
import StepArmareStalpi from "./StepArmareStalpi";
import StepCombinatii from "./StepCombinatii";
import StepDrift from "./StepDrift";
import StepEforturi from "./StepEforturi";
import StepFem from "./StepFem";
import StepFundatie from "./StepFundatie";
import StepGeo from "./StepGeo";
import StepIncarcari from "./StepIncarcari";
import StepModel from "./StepModel";
import StepOptimizare from "./StepOptimizare";
import StepRezistenta from "./StepRezistenta";
import StepSeism from "./StepSeism";
import StepStrategie from "./StepStrategie";

// The focus counter: two clicks on the SAME element must re-apply the
// selection, so each navigation mints a new `n` (see legaturi.ts).
let focusSeq = 0;

// sections that are not chain steps but have a page of their own here
const KNOWN_SECTIONS = new Set([
  "acasa",
  "elemente",
  "element",
  "marca",
  "verificare",
  "fisa",
  "imbunatatiri",
]);

export default function StructureStepRouter({
  section: sectionProp,
}: {
  /** overrides the :section URL param (the element and check-type routes) */
  section?: string;
}) {
  const params = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const registru = useRegistruOptional()?.registru ?? null;
  const section = sectionProp ?? params.section ?? "";
  const data = useStructure();
  const {
    projectId,
    solutie,
    cloneId,
    steps,
    rezumate,
    statePas,
    ruleazaPagina,
    breakpoints,
    runError,
    solutiaCurenta,
    eroare,
  } = data;

  const claseElemente = useMemo(
    () => indexClase(steps["optimizare_structura"]?.result), [steps]);

  // Pagina Pasului 7 arată modelul și când pasul n-a rulat: geometria lui e
  // gata din Pasul 4, iar serverul o construiește fără s-o analizeze (v.
  // `schitaFem.ts`). Cererea stă aici, nu în pagină, ca să nu plece decât
  // când pasul e chiar deschis.
  const { schita: schitaFem, stare: stareSchitaFem } = useSchitaFem(
    projectId, cloneId, steps, rezumate, section === "model_fem");

  // ?focus=<ref>&?nivel=<n>: the element a failed-check link wants selected.
  // Re-minted per navigation (location.key) so a second click re-applies.
  const focusRef = searchParams.get("focus");
  const focusNivel = searchParams.get("nivel");
  const focus: Focus | undefined = useMemo(
    () =>
      focusRef
        ? { ref: focusRef, nivel: focusNivel ? Number(focusNivel) : null, n: ++focusSeq }
        : undefined,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location.key, focusRef, focusNivel]
  );

  const activeStep = STEPS.find((s) => s.key === section);

  if (!activeStep && !KNOWN_SECTIONS.has(section)) {
    return (
      <ObsHost className="ob-main">
        <div className="ob-pagina">
          <div className="sg-error">Pas necunoscut: {section}</div>
        </div>
      </ObsHost>
    );
  }

  // Id-ul din cale nu e al unei soluții de structură: se spune, cu drum spre
  // soluția implicită de pe arhitectura firului (Î24).
  if (eroare) {
    const arh = registru?.implicit.fir.architecture ?? null;
    const implicita = registru
      ? caleEtapa(projectId, "structure", registru.implicit.fir.structure, arh) : null;
    return (
      <ObsHost className="ob-main">
        <div className="ob-pagina" style={{ position: "relative", minHeight: 200 }}>
          <EroareSolutie fel={eroare} href={implicita} />
        </div>
      </ObsHost>
    );
  }

  return (
    <ObsHost className="ob-main">
      {section === "acasa" && <Acasa id={projectId} solutie={solutie} steps={steps} />}
      {section === "elemente" && (
        <ElementeLista id={projectId} solutie={solutie} steps={steps} />
      )}
      {section === "element" && params.ref && (
        <ElementPage
          id={projectId}
          cloneId={cloneId}
          refElement={decodeURIComponent(params.ref)}
          solutie={solutie}
          steps={steps}
        />
      )}
      {section === "marca" && params.marca && (
        <MarcaArmaturaPage
          id={projectId}
          cloneId={cloneId}
          marca={decodeURIComponent(params.marca)}
          solutie={solutie}
        />
      )}
      {section === "verificare" && params.tip && (
        <VerificareTipPage
          id={projectId}
          tip={decodeURIComponent(params.tip)}
          solutie={solutie}
          steps={steps}
        />
      )}
      {section === "fisa" && (
        <FisaSolutiePage
          id={projectId}
          solutie={solutie}
          steps={steps}
          rand={solutiaCurenta}
        />
      )}
      {section === "imbunatatiri" && (
        <div className="ob-pagina">
          <Imbunatatiri />
        </div>
      )}
      {activeStep && (
        <div className="ob-pagina">
          {/* the shared step header: chapter + neighbours + the question from
              catalog_pasi + the verdict indicator (see PasAntet) */}
          <PasAntet id={projectId} solutie={solutie} pasKey={activeStep.key} steps={steps} />
          <div className="pj-step-page">
            <TitluPas cheie={activeStep.key} titlu={titluPas(activeStep.key)} />
            {runError && <div className="sg-error">{runError}</div>}
            {activeStep.key === "studiu_geotehnic" && (
              <StepGeo
                projectId={projectId}
                state={statePas(activeStep.key)}
                running={ruleazaPagina(activeStep.key)}
              />
            )}
            {activeStep.key === "analiza_arhitectura" && (
              <StepArhitectura
                projectId={projectId}
                cloneId={cloneId}
                state={statePas(activeStep.key)}
                running={ruleazaPagina(activeStep.key)}
              />
            )}
            {activeStep.key === "strategie_arhitecturala" && (
              <StepStrategie
                state={statePas(activeStep.key)}
                running={ruleazaPagina(activeStep.key)}
              />
            )}
            {activeStep.key === "fundatie_estimata" && (
              <StepFundatie
                projectId={projectId}
                cloneId={cloneId}
                state={statePas(activeStep.key)}
                running={ruleazaPagina(activeStep.key)}
              />
            )}
            {/* aceeași pagină pentru amândoi pașii fundației — v.
                comentariul din StepRezistenta.tsx */}
            {(activeStep.key === "rezistenta_fundatie"
              || activeStep.key === "verificare_fundatie") && (
              <StepRezistenta
                pas={activeStep.key}
                focus={focus}
                projectId={projectId}
                cloneId={cloneId}
                state={statePas(activeStep.key)}
                running={ruleazaPagina(activeStep.key)}
              />
            )}
            {activeStep.key === "model_structura" && (
              <StepModel
                focus={focus}
                solutie={solutie ?? undefined}
                projectId={projectId}
                cloneId={cloneId}
                rutaId={projectId}
                state={statePas(activeStep.key)}
                running={ruleazaPagina(activeStep.key)}
                // stările înghețate ale Pasului 4 ale rândului privit
                // (`result.breakpoints`), cerute cu `?bp=`
                breakpoints={breakpoints}
                bp={searchParams.get("bp")}
              />
            )}
            {activeStep.key === "model_fem" && (
              <StepFem
                focus={focus}
                projectId={projectId}
                // clasa de execuție a fiecărui element, din Pasul 19: la click
                // pe o piesă se vede și din ce tip face parte
                clase={claseElemente}
                cloneId={cloneId}
                state={statePas(activeStep.key)}
                running={ruleazaPagina(activeStep.key)}
                // modelul se poate VEDEA și înainte de Pasul 7: geometria lui
                // e gata din Pasul 4 (v. `schitaFem.ts`)
                schita={schitaFem}
                schitaSeConstruieste={stareSchitaFem === "se_incarca"}
              />
            )}
            {activeStep.key === "incarcari" && (
              <StepIncarcari
                state={statePas(activeStep.key)}
                femState={steps["model_fem"] ?? null}
                running={ruleazaPagina(activeStep.key)}
              />
            )}
            {activeStep.key === "seism" && (
              <StepSeism
                state={statePas(activeStep.key)}
                running={ruleazaPagina(activeStep.key)}
              />
            )}
            {activeStep.key === "deplasari_laterale" && (
              <StepDrift state={statePas(activeStep.key)} focus={focus} />
            )}
            {activeStep.key === "combinatii" && (
              <StepCombinatii
                state={statePas(activeStep.key)}
                running={ruleazaPagina(activeStep.key)}
              />
            )}
            {activeStep.key === "eforturi" && (
              <StepEforturi
                projectId={projectId}
                cloneId={cloneId}
                rutaId={projectId}
                solutie={solutie}
                state={statePas(activeStep.key)}
                running={ruleazaPagina(activeStep.key)}
              />
            )}
            {activeStep.key === "armare_grinzi" && (
              <StepArmareGrinzi
                focus={focus}
                projectId={projectId}
                solutie={solutie ?? undefined}
                state={statePas(activeStep.key)}
                femState={steps["model_fem"] ?? null}
                running={ruleazaPagina(activeStep.key)}
              />
            )}
            {activeStep.key === "armare_pereti" && (
              <StepArmarePereti
                focus={focus}
                projectId={projectId}
                solutie={solutie ?? undefined}
                state={statePas(activeStep.key)}
                femState={steps["model_fem"] ?? null}
                running={ruleazaPagina(activeStep.key)}
              />
            )}
            {activeStep.key === "armare_stalpi" && (
              <StepArmareStalpi
                focus={focus}
                projectId={projectId}
                solutie={solutie ?? undefined}
                state={statePas(activeStep.key)}
                femState={steps["model_fem"] ?? null}
                // adâncimea de fundare și secțiunea tălpii, pentru vederea
                // cotată a stâlpului întreg (fundația NU se mai desenează ca o
                // cutie fixă): fundația ADOPTATĂ, cum o spune serverul
                fundatieState={pasulFundatiei(steps)}
                running={ruleazaPagina(activeStep.key)}
              />
            )}
            {activeStep.key === "armare_placa" && (
              <StepArmarePlaca
                focus={focus}
                projectId={projectId}
                solutie={solutie ?? undefined}
                state={statePas(activeStep.key)}
                femState={steps["model_fem"] ?? null}
                running={ruleazaPagina(activeStep.key)}
              />
            )}
            {activeStep.key === "armare_fundatie" && (
              <StepArmareFundatie
                focus={focus}
                projectId={projectId}
                solutie={solutie ?? undefined}
                state={statePas(activeStep.key)}
                running={ruleazaPagina(activeStep.key)}
              />
            )}
            {activeStep.key === "optimizare_structura" && (
              <StepOptimizare
                projectId={projectId}
                state={statePas(activeStep.key)}
                running={ruleazaPagina(activeStep.key)}
              />
            )}
          </div>
        </div>
      )}
    </ObsHost>
  );
}
